import React, { useEffect, useContext, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../authProvider.mjs';
import PageShell from '../../components/PageShell.mjs';
import { takeReturnPath } from '../returnPath.mjs';

const AuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshAuthState } = useContext(AuthContext);
  // Once only: the return path is read-once, and a second pass (StrictMode in
  // development runs effects twice) would find it gone and go home instead.
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;
    const handleCallback = async () => {
      const success = searchParams.get('success');
      const error = searchParams.get('error');
      const needsProfileCompletion = searchParams.get('needsProfileCompletion') === 'true';

      if (success === 'true') {
        // Refresh auth state to get the user data from cookies
        await refreshAuthState();

        // Back to the page that sent them to sign in - typically a link in an
        // email - rather than always the home page.
        const back = takeReturnPath();
        if (needsProfileCompletion) {
          navigate('/complete-profile', back ? { state: { returnUrl: back } } : undefined);
        } else {
          navigate(back || '/');
        }
      } else if (error) {
        const message = searchParams.get('message') || 'Authentication failed';
        navigate(`/login?error=${encodeURIComponent(message)}`);
      } else {
        // No success or error parameter, redirect to login
        navigate('/login');
      }
    };

    handleCallback();
  }, [searchParams, navigate, refreshAuthState]);

  return (
    <PageShell variant="entry">
      <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        <div className="w-12 h-12 border-4 border-gray-200 border-t-stanford-red rounded-full animate-spin mx-auto mb-4"></div>
        <h2 className="text-xl font-semibold text-gray-800 mb-2">Completing Authentication</h2>
        <p className="text-gray-600">Please wait while we verify your credentials...</p>
      </div>
    </PageShell>
  );
};

export default AuthCallback;
